<template>
  <div class="vocabulary-results-container">
    <div
      v-if="vocabularyList.length > 0"
      class="results-header"
    >
      <h3>Extracted Vocabulary</h3>
      <FeedbackThumbs
        v-if="resourceId"
        :feature-type="'vocabulary'"
        :target-id="resourceId"
      />
      <div class="action-buttons">
        <Button
          label="Save Selected"
          icon="pi pi-check"
          :disabled="selectedWords.length === 0"
          size="small"
          :loading="isSaving"
          @click="saveSelectedWords"
        />
        <Button
          label="Select All"
          icon="pi pi-check-square"
          severity="secondary"
          size="small"
          @click="selectAll"
        />
        <Button
          label="Deselect All"
          icon="pi pi-times"
          severity="secondary"
          size="small"
          @click="deselectAll"
        />
      </div>
    </div>

    <div
      v-if="vocabularyList.length === 0"
      class="no-results"
    >
      <p>No vocabulary extracted yet.</p>
    </div>

    <div class="vocabulary-list">
      <div
        v-for="(vocab, index) in vocabularyList"
        :key="index"
        class="vocabulary-item"
        :class="{ selected: isWordSelected(vocab) }"
      >
        <Checkbox
          v-model="selectedWords"
          :input-id="`word-${index}`"
          name="vocabulary"
          :value="vocab"
          class="vocab-checkbox"
          @change="updateSelection"
        />
        <label
          :for="`word-${index}`"
          class="word-content"
        >
          <div class="word-header">
            <span class="word">{{ vocab.word }}</span>
            <span class="reading">{{ vocab.reading }}</span>
            <Tag
              v-if="vocab.jlpt && vocab.jlpt.length > 0"
              :value="getJLPTCategory(vocab.jlpt)"
              :severity="getJLPTSeverity(vocab.jlpt)"
              class="jlpt-tag"
            />
          </div>
          <div class="word-meaning">
            {{ formatMeaning(vocab.english_meaning) }}
          </div>
          <div
            v-if="vocab.definition"
            class="word-definition"
          >
            {{ vocab.definition }}
          </div>
        </label>
      </div>
    </div>
  </div>
</template>

<script>
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import FeedbackThumbs from '@/components/feedback/FeedbackThumbs.vue';
import { postWithAuthentication } from '@/services/api';
import {
  BASE_URL,
  SAVE_WORD_ENDPOINT,
} from '@/assets/constants/api_endpoints';
import {
  SAVE_WORD_SUCCESS_MSG,
  SAVE_WORD_FAILED_ERR_MSG,
  SUCCESS_STATUS,
  FAILED_STATUS,
} from '@/assets/constants/constants';

export default {
  name: 'VocabularyExtractionResults',
  components: {
    Checkbox,
    Button,
    Tag,
    FeedbackThumbs,
  },
  props: {
    vocabularyList: {
      type: Array,
      default: () => [],
    },
    resourceId: {
      type: Number,
      default: null,
    },
  },
  emits: ['words-saved', 'selection-changed'],
  data() {
    return {
      selectedWords: [],
      isSaving: false,
    };
  },
  watch: {
    vocabularyList(newVal) {
      // Reset selection when new vocabulary is extracted
      this.selectedWords = [];
    },
  },
  methods: {
    getJLPTCategory(jlptLevels) {
      if (!jlptLevels) return '';
      
      // Convert to array if it's a string
      const levels = Array.isArray(jlptLevels) ? jlptLevels : [jlptLevels];
      
      const hasAdvanced = levels.some(level => ['N1', 'N2'].includes(level));
      const hasIntermediate = levels.some(level => level === 'N3');
      const hasBeginner = levels.some(level => ['N5', 'N4'].includes(level));
      
      if (hasAdvanced) return 'JLPT N2/N1';
      if (hasIntermediate) return 'JLPT N3';
      if (hasBeginner) return 'JLPT N4/N5';
      return '';
    },
    getJLPTSeverity(jlptLevels) {
      const category = this.getJLPTCategory(jlptLevels);
      switch(category) {
        case 'Beginner':
          return 'info';
        case 'Intermediate':
          return 'warn';
        case 'Advanced':
          return 'danger';
        default:
          return 'info';
      }
    },
    formatMeaning(meanings) {
      if (Array.isArray(meanings)) {
        return meanings.join(', ');
      }
      return meanings || '';
    },
    isWordSelected(vocab) {
      return this.selectedWords.some(
        (w) => w.word === vocab.word && w.reading === vocab.reading
      );
    },
    updateSelection() {
      // Trigger parent update if needed
      this.$emit('selection-changed', this.selectedWords);
    },
    selectAll() {
      this.selectedWords = [...this.vocabularyList];
      this.updateSelection();
    },
    deselectAll() {
      this.selectedWords = [];
      this.updateSelection();
    },
    async saveSelectedWords() {
      if (this.selectedWords.length === 0) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Warning',
          detail: 'Please select at least one word to save',
          life: 3000,
        });
        return;
      }

      this.isSaving = true;
      let successCount = 0;
      let failureCount = 0;

      for (const vocab of this.selectedWords) {
        try {
          let jlpt = '';
          if (vocab.jlpt && vocab.jlpt.length > 0) {
            jlpt = vocab.jlpt;
          }

          let definition = '';
          if (vocab.english_meaning) {
            if (Array.isArray(vocab.english_meaning)) {
              definition = vocab.english_meaning[0];
            } else {
              definition = vocab.english_meaning;
            }
          }

          let wordDefinition = '';
          if (vocab.definition) {
            wordDefinition = vocab.definition;
          }

          const resource = this.$root.$store.state.readerDocument || {};

          let payload = {
            word: vocab.word,
            definition: wordDefinition,
            jlpt: jlpt,
            reading: vocab.reading,
            is_sample: resource.is_sample || false,
            resource_id: resource.id.toString(),
            subject_id: resource.subject_id?.toString()
          };

          const response = await postWithAuthentication(
            BASE_URL,
            SAVE_WORD_ENDPOINT,
            payload
          );

          successCount += 1;
          // Mark word as saved
          vocab.saved_word = true;
          console.log(`Word "${vocab.word}" saved successfully. saved_word: ${vocab.saved_word}`);
        } catch (error) {
          console.error('Error saving word:', error);
          failureCount += 1;
        }
      }

      this.isSaving = false;

      // Show result message
      if (successCount > 0) {
        this.$toast.add({
          severity: 'success',
          summary: 'Success',
          detail: `${successCount} word(s) saved successfully${
            failureCount > 0 ? `. ${failureCount} word(s) failed to save.` : '.'
          }`,
          life: 3000,
        });
      }

      if (failureCount > 0 && successCount === 0) {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: SAVE_WORD_FAILED_ERR_MSG,
          life: 3000,
        });
      }

      this.$emit('words-saved', { successCount, failureCount });
      this.selectedWords = [];
    },
  },
};
</script>

<style scoped>
.vocabulary-results-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.results-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e0e0e0;
}

.results-header h3 {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

.no-results {
  padding: 2rem 1rem;
  text-align: center;
  color: #666;
  background-color: #f5f5f5;
  border-radius: 0.25rem;
}

.vocabulary-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 400px;
  overflow-y: auto;
}

.vocabulary-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.vocabulary-item:hover {
  background-color: #f9f9f9;
}

.vocabulary-item.selected {
  background-color: #e3f2fd;
  border-color: #2196f3;
}

.word-content {
  flex: 1;
  cursor: pointer;
}

.word-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
}

.word {
  font-weight: 600;
  font-size: 1rem;
}

.reading {
  color: #666;
  font-size: 0.875rem;
}

.jlpt-tag {
  font-size: 0.75rem;
}

.word-meaning {
  color: #555;
  font-size: 0.875rem;
  line-height: 1.4;
}

.word-definition {
  color: #777;
  font-size: 0.8rem;
  line-height: 1.4;
  margin-top: 0.35rem;
  font-style: italic;
}

:deep(.p-checkbox) {
  min-width: 20px;
  pointer-events: auto !important;
  z-index: 1;
}
</style>
